"use client";

import { getTranslation } from "@/i18n/get-translation";
import { useRouter } from "next/navigation";
import {
  ChangeEvent,
  Dispatch,
  FormEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { IErrorResponse, IVerificationPayload } from "@/types/agenda.type";
import {
  resendVerificationCode,
  sendVerificationCode,
} from "@/services/agenda.service";
import VerificationInputField from "./verification-input-field";
import { useSnackbar } from "notistack";

interface IDoctorAppointmetVerification {
  locale: string;
  confirmationCode?: string;
  appointmentId: number;
  isLoading?: boolean;
  setIsConfirmed: Dispatch<SetStateAction<boolean>>;
  setAppStep: Dispatch<SetStateAction<number>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

interface IVerificationCode {
  n1: string;
  n2: string;
  n3: string;
  n4: string;
  n5: string;
  n6: string;
}

const initialValue: IVerificationCode = {
  n1: "",
  n2: "",
  n3: "",
  n4: "",
  n5: "",
  n6: "",
};

export default function DoctorAppointmetVerification({
  locale,
  confirmationCode,
  appointmentId,
  isLoading,
  setIsConfirmed,
  setAppStep,
  setIsLoading,
}: IDoctorAppointmetVerification) {
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();

  const t = getTranslation(locale, "appointment");
  const tErrors = getTranslation(locale, "errors");

  const [verificationCode, setVerificationCode] =
    useState<IVerificationCode>(initialValue);

  useEffect(() => {
    if (confirmationCode) {
      setVerificationCode({ ...verificationCode, n1: confirmationCode });
    }
  }, [confirmationCode]);

  const hasEmptyValue = useCallback(
    (obj: IVerificationCode): boolean => {
      for (const key in obj) {
        if (
          obj.hasOwnProperty(key) &&
          (obj[key as keyof IVerificationCode] === undefined ||
            obj[key as keyof IVerificationCode] === null ||
            obj[key as keyof IVerificationCode] === "")
        ) {
          return true; // Found a key with an empty value
        }
      }
      return false; // No key with an empty value found
    },
    [verificationCode]
  );

  const handleFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const verificationPayload: IVerificationPayload = {
        status: "confirmed",
        confirmation_code: "",
      };

      if (!hasEmptyValue(verificationCode)) {
        verificationPayload.confirmation_code =
          verificationCode?.n1 +
          verificationCode?.n2 +
          verificationCode?.n3 +
          verificationCode?.n4 +
          verificationCode?.n5 +
          verificationCode?.n6;
      }

      if (verificationPayload.confirmation_code && appointmentId) {
        setIsLoading(true);
        sendVerificationCode(verificationPayload, appointmentId)
          .then((res: boolean | IErrorResponse) => {
            if (typeof res === "boolean" && res) {
              setIsLoading(false);
              setIsConfirmed(true);
              setAppStep(5);
            } else {
              setIsLoading(false);
              enqueueSnackbar(tErrors("went_wrong"), { variant: "error" });
            }
          })
          .catch((error: Error) => {
            if (error?.message) {
              setIsLoading(false);
              enqueueSnackbar(error.message, { variant: "error" });
            }
          });
      }
    },
    [verificationCode]
  );

  const handleResendVerificationCode = useCallback(() => {
    setIsLoading(true);
    resendVerificationCode(appointmentId)
      .then((res: boolean | IErrorResponse) => {
        if (typeof res === "boolean" && res) {
          setIsLoading(false);
          enqueueSnackbar("Code sent successfully!", { variant: "success" });
        } else {
          setIsLoading(false);
          enqueueSnackbar(tErrors("went_wrong"), { variant: "error" });
        }
      })
      .catch((error: Error) => {
        setIsLoading(false);
        enqueueSnackbar(error.message, { variant: "error" });
      });
  }, [appointmentId]);

  const handleGoToNextSibling = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.value) {
        const element = event.target as HTMLInputElement;
        const nextSibling = element.nextElementSibling as HTMLInputElement;
        nextSibling ? nextSibling.focus() : element.blur();
      }
    },
    []
  );

  return (
    <div className="relative text-agenda-purple-800">
      <div>
        {/* <h2 className="text-primary-800 text-2xl">{t("email_verification")}</h2> */}
        <p className="mt-1 mb-2">
          {t("enter_code")}
        </p>
        <p className="mt-1 mb-4">
          {t("code_suggestion")} <b className="font-bold text-agenda-blue-600">{confirmationCode}</b>
        </p>

        <form onSubmit={handleFormSubmit} className="flex flex-wrap text-left">
          <div className="w-full flex gap-2">
            <VerificationInputField
              id="n1"
              name="n1"
              defaultValue={verificationCode.n1}
              disabled={Boolean(confirmationCode)}
            />

            <VerificationInputField
              id="n2"
              name="n2"
              value={verificationCode.n2}
              handleOnChange={(e: ChangeEvent<HTMLInputElement>) => {
                setVerificationCode({
                  ...verificationCode,
                  n2: e.target.value,
                });
                handleGoToNextSibling(e);
              }}
            />

            <VerificationInputField
              id="n3"
              name="n3"
              value={verificationCode.n3}
              handleOnChange={(e: ChangeEvent<HTMLInputElement>) => {
                setVerificationCode({
                  ...verificationCode,
                  n3: e.target.value,
                });
                handleGoToNextSibling(e);
              }}
            />

            <VerificationInputField
              id="n4"
              name="n4"
              value={verificationCode.n4}
              handleOnChange={(e: ChangeEvent<HTMLInputElement>) => {
                setVerificationCode({
                  ...verificationCode,
                  n4: e.target.value,
                });
                handleGoToNextSibling(e);
              }}
            />

            <VerificationInputField
              id="n5"
              name="n5"
              value={verificationCode.n5}
              handleOnChange={(e: ChangeEvent<HTMLInputElement>) => {
                setVerificationCode({
                  ...verificationCode,
                  n5: e.target.value,
                });
                handleGoToNextSibling(e);
              }}
            />

            <VerificationInputField
              id="n6"
              name="n6"
              value={verificationCode.n6}
              handleOnChange={(e: ChangeEvent<HTMLInputElement>) => {
                setVerificationCode({
                  ...verificationCode,
                  n6: e.target.value,
                });
                handleGoToNextSibling(e);
              }}
            />
          </div>
          <div className="w-full">
            <button
              type="submit"
              className="whitespace-nowrap text-white bg-agenda-blue-600 hover:bg-agenda-blue-700 active:scale-95 h-12 rounded-lg py-3 px-8 leading-4 flex items-center justify-center mt-4 font-bold"
            >
              {t("schedule_appointment")}
            </button>
          </div>
        </form>
        <div className="flex mt-4">
          <p className="text-agenda-purple-800">
            {t("email_not_received")}
          </p>
          <button
            className="text-agenda-blue-600 underline ml-2"
            onClick={handleResendVerificationCode}
          >
            {t("resend")}
          </button>
        </div>
      </div>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/70">
          <div className="loader"></div>
        </div>
      )}
    </div>
  );
}
